import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown, ChevronRight, Search,
  Users, Tag, Calendar, RefreshCw, Filter, X, FilterX, Download, User
} from 'lucide-react';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import { useQuery } from '../QueryContext'; // Contexto correcto
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dimassiLogo from '../assets/Dimassi_Logo_Negro.png';


/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID"
};

const formatNumber = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0'; // ✅ Corregido para mostrar $0
  const value = num || 0;
  if (isUSD) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0, // Always show 2 decimals for USD
      maximumFractionDigits: 0
    }).format(value);
  }
  // For non-USD (bultos), show 0 decimal places
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatK = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0'; // Default for empty/zero with decimals
  const value = num || 0;
  if (Math.abs(value) >= 1000) {
    const formattedValue = (value / 1000).toFixed(0); // 0 decimals for 'K'
    return isUSD ? `$${formattedValue}K` : `${formattedValue}K`;
  }
  return formatNumber(value, isUSD);
};
const formatDollar = (num) => formatNumber(num, true);
const formatRegionName = (name) => {
  if (!name) return 'N/A';
  return name.split('(')[0].trim();
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000'; // Rojo Oscuro
  if (val < 90) return '#ff4d4d'; // Rojo Claro
  if (val < 100) return '#ffcc00'; // Amarillo
  return '#00ba52'; // Verde
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  // Ocultar etiquetas de segmentos muy pequeños para evitar solapamiento (opcional, recomendado 3%)
  if (percent < 0.03) return null;

  const RADIAN = Math.PI / 180;
  // Aumentamos ligeramente el radio para que la etiqueta no toque el borde del pastel
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#cbd5e1" // Un gris azulado suave más elegante que el blanco puro
      fontSize="10"
      fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ LÓGICA MEJORADA: "Todos" cuando todos están seleccionados
  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'                          // ✅ ¡MÁGICO!
    : hasSelection
      ? (selectedValues.length === 1
        ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`)
      : 'Todos';                        // ✅ Vacío = Todos

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    console.log(`🔄 ${name}: SelectAll - filtered: ${filteredOptions.length}, total: ${totalOptions}, current: ${selectedValues.length}`);

    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => { // ← useCallback
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input
                type="checkbox"
                checked={areAllSelected}
                onChange={handleSelectAll}
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;

              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => {
              console.log(`🗑️ ${name}: Limpiar`);
              onChange(name, []);
            }}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [],
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE: [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: []
};

const DashboardSugeridos = () => {
  // Totales (para evitar no-undef en ESLint)
  // Si ya tenemos caché, no mostramos loading inicial

  const { salesQuerySugeridos, getSalesFilters, loading: apiLoading } = useQuery();
  const { profile } = useAuth();

  // Define estos estados que te faltaban:
  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [regionActivationTableData, setRegionActivationTableData] = useState([]);
  const [metricType, setMetricType] = useState('BULTOS'); // ✅ Definir estado faltante
  const [showOnlyInactive, setShowOnlyInactive] = useState(false); // 🎯 Estado para filtrar inactivos
  const [expandedRegions, setExpandedRegions] = useState({}); // ✅ NUEVO ESTADO PARA EXPANSIÓN
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay] = useState([]); // ✅ NUEVO ESTADO PARA EL DÍA
  // Changed default year from new Date().getFullYear() to [2024, 2025, 2026] to show all available data
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [sugeridosData, setSugeridosData] = useState([]);
  const [comparisonMode, setComparisonMode] = useState('CUOTA'); // 'CUOTA' or 'FORECAST'
  const [devMode, setDevMode] = useState('NONE'); // NONE, DEV, FIN, NETO
  const [expandedProveedores, setExpandedProveedores] = useState({});
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const [expandedLineas, setExpandedLineas] = useState({});
  const [expandedClasificaciones, setExpandedClasificaciones] = useState({});
  const [expandedSkus, setExpandedSkus] = useState({});
  const [editedSugeridos, setEditedSugeridos] = useState({});
  const [clientStocks, setClientStocks] = useState({});
  const stockAdjustmentFactor = 0.7; // Fijo al 70%
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [selectedClienteLabel, setSelectedClienteLabel] = useState('');

  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [lowCompliance, setLowCompliance] = useState(false);
  const sugeridosTotals = {
    sugeridos: Number(metrics?.sugeridos_total ?? metrics?.total_sugeridos ?? 0),
    pedidos: Number(metrics?.venta_total ?? metrics?.total_pedidos ?? 0),
    usd: Number(metrics?.stock_total ?? metrics?.total_usd ?? 0)
  };

  const toggleProveedor = (proveedor) => {
    setExpandedProveedores(prev => ({
      ...prev,
      [proveedor]: !prev[proveedor]
    }));
  };


  const toggleMarca = (marca) => {
    setExpandedMarcas(prev => ({ ...prev, [marca]: !prev[marca] }));


  };

  const toggleLinea = (linea) => {
    setExpandedLineas(prev => ({
      ...prev,
      [linea]: !prev[linea]
    }));
  };

  const toggleClasificacion = (clasificacion) => {
    setExpandedClasificaciones(prev => ({
      ...prev,
      [clasificacion]: !prev[clasificacion]
    }));
  };

  const toggleSku = (sku) => {
    setExpandedSkus(prev => ({
      ...prev,
      [sku]: !prev[sku]
    }));
  };

  const handleSugeridoChange = (sku, value) => {
    setEditedSugeridos(prev => ({
      ...prev,
      [sku]: Number(value || 0)
    }));
  };

  const handleClientStockChange = (sku, value) => {
    setClientStocks(prev => ({
      ...prev,
      [sku]: value === '' ? '' : Number(value || 0)
    }));
  };

  const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767'];


  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  const [availableYears, setAvailableYears] = useState([]);

  // En loadDashboardData:
  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getSalesFilters();
        // ✅ Manejar el caso donde config es null o undefined
        if (!config) {
          throw new Error("Configuración de ventas no disponible.");
        }
        setSalesConfig(config);

        setAvailableOptions(config.availableOptions || {});
        setAvailableYears(config.availableYears || [currentYear]);

        // 🎯 PREFILTRAR CLI_SEG_DES LOCALMENTE
        const allSegments = config.availableOptions?.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];

        // ✅ SELECCIONAR TODOS MENOS los excluidos
        const prefilteredSegments = allSegments.filter(segment =>
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );

        const prefilteredFilters = {
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          // Para que coincida con la auditoría de "bruto total" (sin recortes de segmento),
          // no precargamos CLI_SEG_DES aquí.
          CLI_SEG_DES: prefilteredSegments
        };

        setFilters(prefilteredFilters);

        console.log('🎯 SEGMENTO PREFILTRADO LOCAL:', {
          todos: allSegments.length,
          seleccionados: prefilteredSegments.length,
          sample: prefilteredSegments.slice(0, 3),
          excluidos: allSegments.filter(s => excludedSegments.some(ex => s.toUpperCase().includes(ex.toUpperCase())))
        });

        // Años y meses
        const defaultYear = config.currentYear || currentYear;
        setSelectedAnios([defaultYear]);
        setSelectedMeses([nombresMeses[new Date().getMonth()]]);

        filtersInitialized.current = true;

      } catch (err) {
        console.error('Error al cargar la configuración del dashboard:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []); // ✅ Solo una vez

  const handleRefresh = async () => {
    clearCache();
    // No necesitamos setLocalLoading(true) aquí porque fetchData maneja setQuerying
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    const config = salesConfig;
    if (!config?.availableOptions?.CLI_SEG_DES) return;

    // 🎯 MISMO PREFILTRADO
    const allSegments = config.availableOptions.CLI_SEG_DES;
    const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefilteredSegments = allSegments.filter(segment =>
      !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
    );

    const resetFilters = {
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      // En "bruto total" no recortamos por segmento.
      CLI_SEG_DES: [excludedSegments]
    };

    setFilters(resetFilters);
    console.log('🔄 Reset Segmento prefiltrado');
  }, [salesConfig]);



  const processResponse = (response) => {
    console.log('🔍 RAW RESPONSE:', response);

    const data = response.sugeridos_data || [];

    return {
      sugeridosMetrics: {
        cuota_total: data.reduce(
          (acc, r) => acc + Number(r.cuota_bultos || 0),
          0
        ),
        venta_total: data.reduce(
          (acc, r) => acc + Number(r.venta_bultos || 0),
          0
        ),
        promedio_3m_total: data.reduce(
          (acc, r) => acc + Number(r.promedio_3m_bultos || 0),
          0
        ),
        sugerido_total: data.reduce(
          (acc, r) => acc + Number(r.sugerido || 0),
          0
        ),
        sugerido_definitivo_total: data.reduce(
          (acc, r) => acc + Number(r.sugerido_definitivo || 0),
          0
        ),
        stock_total: data.reduce(
          (acc, r) => acc + Number(r.stock_disponible || 0),
          0
        )
      },
      sugeridosData: data
    };
  };

  const exportToExcel = (data, filename, type) => {
    let rows = [];

    if (type === 'generic') {
      rows = data.map(item => ({
        Nombre: item.CLI_PRIN || item.CLI_VEN_DES || item.BI_MARCA || item.BI_PROVEEDOR,
        Cuota: Math.round(item.cuota || 0),
        Bultos: Math.round(item.venta || 0), // Bultos
        Efectividad_Pct: Math.round(item.efectividad || 0)
      }));
    } else if (type === 'hierarchical') {
      data.forEach(m => {
        m.skus.forEach(s => {
          rows.push({
            Marca: m.BI_MARCA, SKU: s.sku,
            Cuota: Math.round(s.cuota || 0), Bultos: Math.round(s.venta || 0),
            Efectividad_Pct: Math.round(s.efectividad || 0)
          });
        });
      });
    }
    else if (type === 'regionActivation') {
      rows = data.map(item => ({
        Región: item.region,
        Activos_Cantidad: item.active_qty,
        Activos_Ventas: item.active_sales,
        Inactivos_Cantidad: item.inactive_qty,
        Inactivos_Ventas: item.inactive_sales,
        'Conc. Inactividad (%)': item.total_clients_in_region > 0 ? (item.inactive_qty / item.total_clients_in_region) * 100 : 0, // NEW
        Total_Clientes_5_Meses: item.total_clients_in_region // NEW
      }));
    } else if (type === 'clientDetail') {
      rows = data.map(c => ({
        Región: c.region,
        Cliente: c.client_name,
        Status: c.status,
        Venta_Mes: c.sales_current_month,
        Oportunidad: c.quota_current_month,
        Vendedor: c.salesperson
      }));
    }



    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  };

  // =====================================
  // DESCARGA DE PROFORMA EN PDF CON jsPDF
  // =====================================

  const downloadSugeridoPDF = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const formattedTime = today.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    if (!selectedClienteId) {
      alert('Selecciona un CLIENTE_ID (y su Cliente) para descargar la proforma del sugerido.');
      return;
    }

    const doc = new jsPDF({
      compress: true // Fuerza a jsPDF a usar algoritmos de compresión Flate
    });
    const rows = [];
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 10;

    // =====================================
    // CONFIGURACIÓN DE TIPOGRAFÍA SEGURA
    // =====================================
    const montserratRegularBase64 = "";
    const montserratBoldBase64 = "";

    let fuenteActiva = "helvetica";

    if (montserratRegularBase64 && montserratBoldBase64) {
      try {
        doc.addFileToVFS('Montserrat-Regular.ttf', montserratRegularBase64);
        doc.addFont('Montserrat-Regular.ttf', 'Montserrat', 'normal');
        doc.addFileToVFS('Montserrat-Bold.ttf', montserratBoldBase64);
        doc.addFont('Montserrat-Bold.ttf', 'Montserrat', 'bold');
        fuenteActiva = "Montserrat";
      } catch (e) {
        console.error("Error cargando Montserrat, usando fallback helvetica:", e);
        fuenteActiva = "helvetica";
      }
    }

    doc.setFont(fuenteActiva, 'normal');

    // =====================================
    // ENCABEZADO DIMASSI
    // =====================================
    if (dimassiLogo) {
      doc.addImage(
        dimassiLogo,
        'SVG',
        14,
        currentY,
        45,
        0
      );
    }

    const centroBloqueDerechoX = pageWidth - 70;

    doc.setFont(fuenteActiva, 'bold');
    doc.setFontSize(10);
    doc.text(
      'DIMASSI, C.A.',
      centroBloqueDerechoX,
      currentY + 2,
      { align: 'center' }
    );

    doc.setFont(fuenteActiva, 'normal');
    doc.setFontSize(6.2);
    doc.text(
      'Domicilio Fiscal: Av. Principal de La Castellana, Calle Blandin, Edif. Torre Digitel, Piso 12. Of. B.',
      centroBloqueDerechoX,
      currentY + 6,
      { align: 'center' }
    );
    doc.text(
      'Caracas (Chacao) Miranda. Zona Postal 1060',
      centroBloqueDerechoX,
      currentY + 9,
      { align: 'center' }
    );
    doc.text(
      'www.dimassicorp.com     @dimassivzla',
      centroBloqueDerechoX,
      currentY + 12,
      { align: 'center' }
    );

    currentY += 22;

    // =====================================
    // TÍTULO Y METADATOS DEL DOCUMENTO
    // =====================================
    doc.setFontSize(11);
    doc.setFont(fuenteActiva, 'bold');
    doc.text('PROFORMA DE SUGERIDO', 14, currentY);

    doc.setFont(fuenteActiva, 'normal');
    doc.setFontSize(8.5);
    doc.text(
      `Fecha: ${formattedDate}`,
      pageWidth - 14,
      currentY,
      { align: 'right' }
    );

    currentY += 6;

    doc.text(
      `Cliente: ${selectedClienteLabel}`,
      14,
      currentY
    );
    doc.text(
      `Hora: ${formattedTime}`,
      pageWidth - 14,
      currentY,
      { align: 'right' }
    );

    currentY += 8;

    // =====================================
    // PROCESAMIENTO DE FILAS Y LOGÍSTICA
    // =====================================
    let totalBultos = 0;
    let totalUSD = 0;
    let totalBS = 0;

    // Helper local para formatear monedas con . en miles y , en decimales
    const formatearMoneda = (valor) => {
      return valor.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    adjustedData.forEach(row => {
      const sku = row.id_articulo;
      const sugeridoDef = Number(row.sugerido_definitivo || 0);
      const precioUSD = Number(row.in_pr_usd || 0);
      const precioBS = Number(row.in_pr_bs || 0);
      const montoUSD = sugeridoDef * precioUSD;
      const montoBS = sugeridoDef * precioBS;

      // Only include rows with a suggested amount > 0
      if (sugeridoDef > 0) {
        totalBultos += sugeridoDef;
        totalUSD += montoUSD;
        totalBS += montoBS;
        rows.push([
          sku,
          row.producto,
          sugeridoDef.toFixed(1).replace('.', ','),
          `$ ${formatearMoneda(precioUSD)}`,
          `$ ${formatearMoneda(montoUSD)}`,
          `Bs. ${formatearMoneda(precioBS)}`,
          `Bs. ${formatearMoneda(montoBS)}`
        ]);
      }
    });

    // =====================================
    // CONFIGURACIÓN DE TABLA COMPACTA Y MODERNA
    // =====================================
    autoTable(doc, {
      startY: currentY,
      head: [[
        'SKU',
        'Producto',
        'Cant. Bultos',
        'Precio USD',
        'Total USD',
        'Precio Bs.',
        'Total Bs.'
      ]],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: [31, 41, 55],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        font: fuenteActiva,
        halign: 'left'
      },
      columnStyles: {
        0: { cellWidth: 22 },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' }
      },
      styles: {
        fontSize: fuenteActiva === "Montserrat" ? 7 : 7.5,
        cellPadding: 1.5,
        font: fuenteActiva
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      margin: { left: 14, right: 14 }
    });

    // =====================================
    // BLOQUE DE TOTALES CORPORATIVOS FINALES
    // =====================================
    const finalY = doc.lastAutoTable.finalY + 8;
    const derechaX = pageWidth - 14;

    if (finalY > doc.internal.pageSize.getHeight() - 35) {
      doc.addPage();
      currentY = 20;
    } else {
      currentY = finalY;
    }

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 90, currentY, derechaX, currentY);

    currentY += 6;
    doc.setFontSize(9.5);

    // Total Bultos (Mantiene formato de millares si es un lote grande, pero con 1 decimal)
    doc.setFont(fuenteActiva, 'normal');
    doc.text('Total Bultos Sugeridos:', derechaX - 35, currentY, { align: 'right' });
    doc.setFont(fuenteActiva, 'bold');
    doc.text(`${totalBultos.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`, derechaX, currentY, { align: 'right' });

    // Total USD
    currentY += 5.5;
    doc.setFont(fuenteActiva, 'normal');
    doc.text('Monto Total USD:', derechaX - 35, currentY, { align: 'right' });
    doc.setFont(fuenteActiva, 'bold');
    doc.text(`$ ${formatearMoneda(totalUSD)}`, derechaX, currentY, { align: 'right' });

    // Total Bs
    currentY += 5.5;
    doc.setFont(fuenteActiva, 'normal');
    doc.text('Monto Total Bs.:', derechaX - 35, currentY, { align: 'right' });
    doc.setFont(fuenteActiva, 'bold');
    doc.text(`Bs. ${formatearMoneda(totalBS)}`, derechaX, currentY, { align: 'right' });

    const sanitizedLabel = selectedClienteLabel.replace(/[^a-zA-Z0-9]/g, '_') || selectedClienteId || 'Cliente';
    doc.save(`Sugerido_${sanitizedLabel}.pdf`);
  };

  // =====================================
  // Fin PDF
  // =====================================

  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;

    // 1. Forzar la selección de cliente
    if (!selectedClienteId) {
      setSugeridosData([]);
      setMetrics({});
      setQuerying(false);
      return;
    }
    const requestId = ++requestRef.current;
    setQuerying(true);

    // Resetear ediciones manuales al realizar una nueva consulta (cambio de cliente o refresh)
    setEditedSugeridos({});
    setClientStocks({});

    try {
      const safeAnios = Array.isArray(selectedAnios) ? selectedAnios : [];
      const safeMeses = Array.isArray(selectedMeses) ? selectedMeses : [];

      const payload = {
        metric_type: metricType,
        comparison_mode: comparisonMode,
        dev_mode: devMode,

        page_anios: safeAnios.filter(Boolean),
        page_meses: safeMeses.filter(Boolean),
        page_dias: selectedDay.filter(Boolean),

        filters,

        breakdown_type: 'PROVEEDOR',
        chart_year: safeAnios?.[0] || null,
        low_compliance: lowCompliance,

        user_info: profile
          ? {
            username: profile.username,
            cargo: profile.cargo
          }
          : null
      };

      console.log('📤 Payload con USER_INFO:', {
        ...payload,
        user_info: payload.user_info
          ? `${payload.user_info.username} (${payload.user_info.cargo})`
          : 'Sin usuario'
      });

      console.log('📤 Payload completo:', {
        ...payload,
        filters_summary: {
          total_filters: Object.keys(filters).length,
          active_filters: Object.values(filters).filter(arr => arr.length > 0).length,
          v_anulada: filters.V_ANULADA,
          cli_inactivo: filters.CLI_INACTIVO,
          dynamic_count: Object.entries(filters)
            .filter(([k]) => !['V_ANULADA', 'CLI_INACTIVO'].includes(k))
            .reduce((sum, [, v]) => sum + v.length, 0)
        }
      });

      const rawResponse = await salesQuerySugeridos(payload);

      console.log('📥 Backend response:', rawResponse);

      // Evita race conditions
      if (requestId !== requestRef.current) return;

      const processed = processResponse(rawResponse);

      // KPIs
      setMetrics(processed.sugeridosMetrics);

      // Tabla principal
      setSugeridosData(processed.sugeridosData);

    } catch (err) {
      if (requestId === requestRef.current) {
        console.error('❌ Error fetchData:', err);
      }
    } finally {
      if (requestId === requestRef.current) {
        setQuerying(false);
      }
    }
  }, [
    metricType,
    comparisonMode,
    devMode,
    selectedMeses,
    selectedAnios,
    selectedDay,
    filters,
    lowCompliance,
    profile,
    salesQuerySugeridos
  ]);
  // 🔍 DEBUG: Muestra qué filtros tienes disponibles
  useEffect(() => {
    console.log('🔍 availableOptions completo:', availableOptions);

    // ← FORZAR todos los filtros del filterLabels si no llegan del backend
    if (Object.keys(availableOptions).length === 0 && filtersInitialized.current) {
      const allFilterKeys = Object.keys(filterLabels);
      const fullOptions = {};

      allFilterKeys.forEach(key => {
        fullOptions[key] = ['Opción 1', 'Opción 2', 'Opción 3']; // ← Placeholder
      });

      setAvailableOptions(fullOptions);
    }
  }, [availableOptions, filtersInitialized.current]);

  const cleanData = (sugeridosData || []).filter(row => {
    const tieneNombre =
      row.proveedor &&
      row.proveedor !== 'N/A' &&
      row.proveedor !== 'null';

    const tieneDatos =
      Number(row.cuota_bultos || 0) > 0 ||
      Number(row.venta_bultos || 0) > 0 ||
      Number(row.promedio_3m_bultos || 0) > 0 ||
      Number(row.sugerido || 0) > 0 ||
      Number(row.sugerido_definitivo || 0) > 0;

    return tieneNombre && tieneDatos;
  });

  const adjustedData = cleanData.map(row => {
    const sku = row.id_articulo || row.ART_ID;
    const clientStockRaw = clientStocks[sku];
    const hasClientStock = clientStockRaw !== undefined && clientStockRaw !== '';
    const clientStockVal = hasClientStock ? Number(clientStockRaw) : 0;
    const originalSugerido = Number(row.sugerido || 0);
    const adjustedSugerido = hasClientStock
      ? Math.max(0, originalSugerido - clientStockVal * stockAdjustmentFactor)
      : originalSugerido;
    const adjustedSugeridoDef = editedSugeridos[sku] ?? adjustedSugerido;

    return {
      ...row,
      sugerido: adjustedSugerido,
      sugerido_definitivo: adjustedSugeridoDef,
      client_stock: hasClientStock ? clientStockVal : 0,
      client_stock_raw: clientStockRaw !== undefined ? clientStockRaw : ''
    };
  });

  // Debounced auto-fetch - Solo ejecuta si no está querying
  // ✅ NUEVO useEffect con debounce correcto:
  const debouncedFetch = useRef();

  useEffect(() => {
    if (!filtersInitialized.current) return;

    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }

    debouncedFetch.current = setTimeout(() => {
      fetchData();
    }, 300);

    return () => {
      if (debouncedFetch.current) {
        clearTimeout(debouncedFetch.current);
      }
    };
  }, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, metricType]);

  // Variantes para la animación de entrada/salida
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  // Tooltip personalizado para el gráfico de barras
  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', border: '1px solid #ccc' }}>
          <p style={{ color: '#333', fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
          <p style={{ color: '#1a2c4e', fontSize: '0.8rem', margin: 0 }}>{comparisonMode === 'FORECAST' ? 'Forecast' : 'Cuota'}: {formatNumber(payload[0].value)}</p>
          <p style={{ color: '#0068b3', fontSize: '0.8rem', margin: 0 }}>Bultos: {formatNumber(payload[1].value)}</p>
          <p style={{ color: '#00ba52', fontSize: '0.8rem', margin: '5px 0 0', fontWeight: 'bold' }}>Logro: {payload[2].value}%</p>
        </div>
      );
    }
    return null;
  };

  const filterTitle = ` - ${selectedMeses.join(', ')} - ${selectedAnios.join(', ')}${selectedDay.length > 0 ? ` - Día ${selectedDay.join(', ')}` : ''}`; // ✅ Mostrar día

  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Cargando datos del servidor..." />
      ) : null}
      {querying && (
        <div className="querying-overlay active" />
      )}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">
              Sugeridos{/* ✅ Título dinámico */}
            </h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">

            <button
              className="refresh-button"
              onClick={handleRefresh}
              title="Actualizar Datos"
              disabled={querying}
            >
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button
              className="mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>

            <div className="date-filters-group">
              <MultiSelectFilter
                label="Mes"
                name="meses"
                icon={Calendar}
                options={nombresMeses}
                selectedValues={selectedMeses}
                onChange={(name, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año"
                name="anios"
                icon={Calendar}
                options={availableYears.map(year => ({ value: year, label: year.toString() }))}
                selectedValues={selectedAnios}
                onChange={(name, vals) => setSelectedAnios(vals)}
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {(() => {
          const timeVsSalesDiff = parseFloat(metrics.porcentaje) - parseFloat(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
            </div>
          );
        })()}

        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {/* ← DEBUG TEMPORAL: */}

            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') ? Users : Tag}
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => {
                  console.log(`🔄 Cambio filtro ${name}:`, vals.slice(0, 3));
                  setFilters(prev => {
                    const newFilters = { ...prev, [name]: vals };
                    console.log(`✅ setFilters ATÓMICO ${name}:`, newFilters[name].length);
                    return newFilters;
                  });
                }}
              />
            ))}
            {/* Nuevo filtro de bajo cumplimiento - Integrado visualmente */}
            <div className="filter-pill-container"> {/* Contenedor para mantener el ancho */}
              <button className={`filter-pill-button ${lowCompliance ? 'active low-compliance' : ''}`} onClick={() => setLowCompliance(!lowCompliance)} title="Filtrar por cumplimiento menor al 80%">
                <span className="pill-label">Cumplimiento &lt; 80%</span>
              </button>
            </div>
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters} title="Restablecer filtros">
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* TABLA SUGERIDOS */}
        <div
          className="detail-table-container2"
          style={{
            opacity: querying ? 0.8 : 1,
            transition: 'opacity 0.3s',
            marginTop: '20px'
          }}
        >
          <div
            className="chart-container mini table-fixed-height"
            style={{ flex: 1, gridColumn: 'span 2' }}
          >
            <div className="table-header-container">
              <h3 className="chart-title">Sugeridos por Casa</h3>

              <div className="chart-header-filters">
                <button
                  className="export-table-btn"
                  onClick={() =>
                    exportToExcel(sugeridosData, 'Sugeridos_Casa', 'sugeridos')
                  }
                >
                  <Download size={12} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '4px', width: '100%', maxWidth: 420 }}>
                <MultiSelectFilter
                  label="Cliente"
                  name="clienteId"
                  icon={User}
                  options={availableOptions?.CLIENTE_DISPLAY || []}

                  selectedValues={selectedClienteId ? [selectedClienteId] : []}

                  onChange={(name, vals) => {
                    const id = vals?.[0] || null;

                    setSelectedClienteId(id || null);

                    const clienteEncontrado = (availableOptions?.CLI_PRIN || []).find(
                      x => String(x?.value ?? x) === String(id)
                    );

                    // Actualizamos el label del cliente seleccionado con el formato completo encontrado
                    setSelectedClienteLabel(
                      (availableOptions?.CLIENTE_DISPLAY || [])
                        .find(x => String(x.value) === String(id))
                        ?.label || id
                    );
                    setFilters(prev => ({
                      ...prev,
                      CLIENTE_ID: id ? [id] : []
                    }));
                  }}
                />
              </div>
              <button
                className="export-table-btn"
                onClick={downloadSugeridoPDF}
                disabled={!selectedClienteId}
                style={!selectedClienteId ? { opacity: 0.5 } : undefined}
              >
                PDF
              </button>
            </div>

            <div className="minimal-table-wrapper">

              {/* HEADER */}
              <div className="table-header4">
                <div className="col-name">Casa</div>
                <div className="col-data">Cuota BL</div>
                <div className="col-data">Venta Mes</div>
                <div className="col-data">Prom. 3M</div>
                <div className="col-data">Sugerido</div>
                <div className="col-data">Sug. Def.</div>
                <div className="col-data">Stock</div>
                <div className="col-data">Stock Cliente</div>
              </div>

              {/* BODY */}
              <div className="table-body2">

                {!selectedClienteId ? (
                  <div style={{ padding: 80, textAlign: 'center', opacity: 0.7, color: '#ffffff' }}>
                    Selecciona un Id de Cliente para ver los sugeridos
                  </div>
                ) : (
                  (() => {

                    const grouped = (adjustedData || []).reduce((acc, row) => {
                      const prov = row.proveedor || 'SIN PROVEEDOR';
                      const marca = row.marca || 'SIN MARCA';
                      const linea = row.linea || 'SIN LINEA';
                      const clasif = row.clasificacion || 'SIN CLASIFICACION';
                      const sku = row.id_articulo || row.ART_ID || 'SIN SKU';

                      if (!acc[prov]) {
                        acc[prov] = {
                          proveedor: prov,
                          cuota_bultos: 0,
                          venta_bultos: 0,
                          promedio_3m_bultos: 0,
                          sugerido: 0,
                          sugerido_definitivo: 0,
                          stock: 0,
                          client_stock: 0,
                          marcas: {}
                        };
                      }

                      const p = acc[prov];

                      p.cuota_bultos += Number(row.cuota_bultos || 0);
                      p.venta_bultos += Number(row.venta_bultos || 0);
                      p.promedio_3m_bultos += Number(row.promedio_3m_bultos || 0);
                      p.sugerido += Number(row.sugerido || 0);
                      p.sugerido_definitivo += Number(row.sugerido_definitivo || 0);
                      p.stock += Number(row.stock_disponible || 0);
                      p.client_stock += Number(row.client_stock || 0);

                      if (!p.marcas[marca]) {
                        p.marcas[marca] = {
                          marca,
                          cuota_bultos: 0,
                          venta_bultos: 0,
                          promedio_3m_bultos: 0,
                          sugerido: 0,
                          sugerido_definitivo: 0,
                          stock: 0,
                          client_stock: 0,
                          lineas: {}
                        };
                      }

                      const m = p.marcas[marca];

                      m.cuota_bultos += Number(row.cuota_bultos || 0);
                      m.venta_bultos += Number(row.venta_bultos || 0);
                      m.promedio_3m_bultos += Number(row.promedio_3m_bultos || 0);
                      m.sugerido += Number(row.sugerido || 0);
                      m.sugerido_definitivo += Number(row.sugerido_definitivo || 0);
                      m.stock += Number(row.stock_disponible || 0);
                      m.client_stock += Number(row.client_stock || 0);

                      if (!m.lineas[linea]) {
                        m.lineas[linea] = {
                          linea,
                          cuota_bultos: 0,
                          venta_bultos: 0,
                          promedio_3m_bultos: 0,
                          sugerido: 0,
                          sugerido_definitivo: 0,
                          stock: 0,
                          client_stock: 0,
                          clasificaciones: {}
                        };
                      }

                      const l = m.lineas[linea];

                      l.cuota_bultos += Number(row.cuota_bultos || 0);
                      l.venta_bultos += Number(row.venta_bultos || 0);
                      l.promedio_3m_bultos += Number(row.promedio_3m_bultos || 0);
                      l.sugerido += Number(row.sugerido || 0);
                      l.sugerido_definitivo += Number(row.sugerido_definitivo || 0);
                      l.stock += Number(row.stock_disponible || 0);
                      l.client_stock += Number(row.client_stock || 0);

                      if (!l.clasificaciones[clasif]) {
                        l.clasificaciones[clasif] = {
                          clasificacion: clasif,
                          cuota_bultos: 0,
                          venta_bultos: 0,
                          promedio_3m_bultos: 0,
                          sugerido: 0,
                          sugerido_definitivo: 0,
                          stock: 0,
                          client_stock: 0,
                          skus: {}
                        };
                      }

                      const c = l.clasificaciones[clasif];

                      c.cuota_bultos += Number(row.cuota_bultos || 0);
                      c.venta_bultos += Number(row.venta_bultos || 0);
                      c.promedio_3m_bultos += Number(row.promedio_3m_bultos || 0);
                      c.sugerido += Number(row.sugerido || 0);
                      c.sugerido_definitivo += Number(row.sugerido_definitivo || 0);
                      c.stock += Number(row.stock_disponible || 0);
                      c.client_stock += Number(row.client_stock || 0);

                      if (!c.skus[sku]) {
                        c.skus[sku] = {
                          sku,
                          producto: row.producto || 'SIN PRODUCTO',
                          cuota_bultos: 0,
                          venta_bultos: 0,
                          promedio_3m_bultos: 0,
                          sugerido: 0,
                          sugerido_definitivo: 0,
                          stock: 0,
                          client_stock: 0
                        };
                      }

                      const s = c.skus[sku];

                      s.cuota_bultos += Number(row.cuota_bultos || 0);
                      s.venta_bultos += Number(row.venta_bultos || 0);
                      s.promedio_3m_bultos += Number(row.promedio_3m_bultos || 0);
                      s.sugerido += Number(row.sugerido || 0);
                      s.sugerido_definitivo += Number(row.sugerido_definitivo || 0);
                      s.stock += Number(row.stock_disponible || 0);
                      s.client_stock += Number(row.client_stock || 0);

                      return acc;
                    }, {});

                    const providers = Object.values(grouped).map(p => ({
                      ...p,
                      marcas: Object.values(p.marcas).map(m => ({
                        ...m,
                        lineas: Object.values(m.lineas).map(l => ({
                          ...l,
                          clasificaciones: Object.values(l.clasificaciones).map(c => ({
                            ...c,
                            skus: Object.values(c.skus)
                          }))
                        }))
                      }))
                    }));

                    return providers.map((prov) => {

                      const provExpanded = !!expandedProveedores[prov.proveedor];

                      return (
                        <React.Fragment key={prov.proveedor}>

                          {/* PROVEEDOR */}
                          <div
                            className="table-row4 advisor-row"
                            onClick={() => toggleProveedor(prov.proveedor)}
                          >
                            <div className="col-name">
                              {provExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {prov.proveedor}
                            </div>

                            <div className="col-data">{formatNumber(prov.cuota_bultos)}</div>
                            <div className="col-data">{formatNumber(prov.venta_bultos)}</div>
                            <div className="col-data">{formatNumber(prov.promedio_3m_bultos)}</div>
                            <div className="col-data">{formatNumber(prov.sugerido)}</div>
                            <div className="col-data">{formatNumber(prov.sugerido_definitivo)}</div>
                            <div className="col-data">{formatNumber(prov.stock)}</div>
                            <div className="col-data">{formatNumber(prov.client_stock)}</div>
                          </div>

                          {/* MARCAS */}
                          {provExpanded && prov.marcas.map((mar) => {

                            const marExpanded = !!expandedMarcas[mar.marca];

                            return (
                              <React.Fragment key={mar.marca}>

                                <div className="table-row4" onClick={() => toggleMarca(mar.marca)}>
                                  <div className="col-name" style={{ paddingLeft: 20 }}>
                                    {marExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                    {mar.marca}
                                  </div>

                                  <div className="col-data">{formatNumber(mar.cuota_bultos)}</div>
                                  <div className="col-data">{formatNumber(mar.venta_bultos)}</div>
                                  <div className="col-data">{formatNumber(mar.promedio_3m_bultos)}</div>
                                  <div className="col-data">{formatNumber(mar.sugerido)}</div>
                                  <div className="col-data">{formatNumber(mar.sugerido_definitivo)}</div>
                                  <div className="col-data">{formatNumber(mar.stock)}</div>
                                  <div className="col-data">{formatNumber(mar.client_stock)}</div>
                                </div>

                                {/* LINEAS */}
                                {marExpanded && mar.lineas.map((lin) => {

                                  const linExpanded = !!expandedLineas[lin.linea];

                                  return (
                                    <React.Fragment key={lin.linea}>

                                      <div className="table-row4" onClick={() => toggleLinea(lin.linea)}>
                                        <div className="col-name" style={{ paddingLeft: 40 }}>
                                          {linExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                          {lin.linea}
                                        </div>

                                        <div className="col-data">{formatNumber(lin.cuota_bultos)}</div>
                                        <div className="col-data">{formatNumber(lin.venta_bultos)}</div>
                                        <div className="col-data">{formatNumber(lin.promedio_3m_bultos)}</div>
                                        <div className="col-data">{formatNumber(lin.sugerido)}</div>
                                        <div className="col-data">{formatNumber(lin.sugerido_definitivo)}</div>
                                        <div className="col-data">{formatNumber(lin.stock)}</div>
                                        <div className="col-data">{formatNumber(lin.client_stock)}</div>
                                      </div>

                                      {/* CLASIFICACIONES */}
                                      {linExpanded && lin.clasificaciones.map((c) => {

                                        const cExpanded = !!expandedClasificaciones[c.clasificacion];

                                        return (
                                          <React.Fragment key={c.clasificacion}>

                                            <div className="table-row4" onClick={() => toggleClasificacion(c.clasificacion)}>
                                              <div className="col-name" style={{ paddingLeft: 60 }}>
                                                {cExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                {c.clasificacion}
                                              </div>

                                              <div className="col-data">{formatNumber(c.cuota_bultos)}</div>
                                              <div className="col-data">{formatNumber(c.venta_bultos)}</div>
                                              <div className="col-data">{formatNumber(c.promedio_3m_bultos)}</div>
                                              <div className="col-data">{formatNumber(c.sugerido)}</div>
                                              <div className="col-data">{formatNumber(c.sugerido_definitivo)}</div>
                                              <div className="col-data">{formatNumber(c.stock)}</div>
                                              <div className="col-data">{formatNumber(c.client_stock)}</div>
                                            </div>

                                            {/* SKU */}
                                            {cExpanded && c.skus.map((s) => {

                                              const sugeridoFinal =
                                                editedSugeridos[s.sku] ?? Number(s.sugerido_definitivo || 0);

                                              return (
                                                <div key={s.sku} className="table-row4">

                                                  <div className="col-name" style={{ paddingLeft: 80 }}>
                                                    {s.sku} - {s.producto}
                                                  </div>

                                                  <div className="col-data">{formatNumber(s.cuota_bultos)}</div>
                                                  <div className="col-data">{formatNumber(s.venta_bultos)}</div>
                                                  <div className="col-data">{formatNumber(s.promedio_3m_bultos)}</div>
                                                  <div className="col-data">{formatNumber(s.sugerido)}</div>

                                                  <div className="col-data">
                                                    <input
                                                      type="number"
                                                      value={sugeridoFinal}
                                                      onChange={(e) =>
                                                        handleSugeridoChange(s.sku, e.target.value)
                                                      }
                                                      style={{
                                                        width: 70,
                                                        textAlign: 'center',
                                                        border: sugeridoFinal > s.stock ? '2px solid red' : '1px solid #ccc'
                                                      }}
                                                    />
                                                  </div>

                                                  <div className="col-data"
                                                    style={{
                                                      color: sugeridoFinal > s.stock ? 'red' : 'inherit',
                                                      fontWeight: sugeridoFinal > s.stock ? 'bold' : 'normal'
                                                    }}
                                                  >
                                                    {formatNumber(s.stock)}
                                                  </div>

                                                  <div className="col-data">
                                                    <input
                                                      type="number"
                                                      value={clientStocks[s.sku] ?? ''}
                                                      onChange={(e) =>
                                                        handleClientStockChange(s.sku, e.target.value)
                                                      }
                                                      placeholder="0"
                                                      style={{
                                                        width: 70,
                                                        textAlign: 'center',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '4px',
                                                        background: '#1e293b',
                                                        color: '#fff'
                                                      }}
                                                    />
                                                  </div>

                                                </div>
                                              );
                                            })}

                                          </React.Fragment>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}

                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    });

                  })()
                )}

              </div>

              {/* FOOTER */}
              <div className="table-footer4">
                <div className="col-name">TOTALES</div>

                {(() => {
                  const totalCuota = adjustedData.reduce((a, r) => a + Number(r.cuota_bultos || 0), 0);
                  const totalVenta = adjustedData.reduce((a, r) => a + Number(r.venta_bultos || 0), 0);
                  const totalProm = adjustedData.reduce((a, r) => a + Number(r.promedio_3m_bultos || 0), 0);
                  const totalSug = adjustedData.reduce((a, r) => a + Number(r.sugerido || 0), 0);
                  const totalSugDef = adjustedData.reduce((a, r) => a + Number(r.sugerido_definitivo || 0), 0);
                  const totalStock = adjustedData.reduce((a, r) => a + Number(r.stock_disponible || 0), 0);
                  const totalClientStock = adjustedData.reduce((a, r) => a + Number(r.client_stock || 0), 0);

                  return (
                    <>
                      <div className="col-data">{formatNumber(totalCuota)}</div>
                      <div className="col-data">{formatNumber(totalVenta)}</div>
                      <div className="col-data">{formatNumber(totalProm)}</div>
                      <div className="col-data">{formatNumber(totalSug)}</div>
                      <div className="col-data">{formatNumber(totalSugDef)}</div>
                      <div className="col-data">{formatNumber(totalStock)}</div>
                      <div className="col-data">{formatNumber(totalClientStock)}</div>
                    </>
                  );
                })()}
              </div>

            </div>
          </div>
        </div>

        <footer className="status-footer">
          {querying
            ? "SINCRONIZANDO..."
            : `VISTA ACTUAL: ${selectedMeses.join(', ')} ${selectedAnios.join(', ')}`
          }
        </footer>

      </motion.div>
    </motion.div>
  );
};

export default DashboardSugeridos;